import React, { useState, useEffect, useRef } from 'react';
import { getTypeIcon, getPriorityColor } from './boardUtils';
import { useAuth } from '../../contexts/AuthContext';
import { useTickets } from '../../contexts/TicketContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTranslation } from '../../translations';
import { TrashIcon, PencilIcon, EllipsisVerticalIcon, EyeIcon } from '@heroicons/react/24/outline';

const TicketCard = ({ ticket, onClick, isDragging = false }) => {
  const [showAdminMenu, setShowAdminMenu] = useState(false);
  const [contextMenu, setContextMenu] = useState({ visible: false, x: 0, y: 0 });
  const { user } = useAuth();
  const { deleteTicket, updateTicketStatus, projectStatuses } = useTickets();
  const { language } = useLanguage();
  const { t } = useTranslation(language);
  const menuRef = useRef(null);
  const contextMenuRef = useRef(null);

  // Admin/yönetici kontrolü
  const adminRoles = ['Admin', 'Manager', 'IT Müdürü', 'Sistem Yöneticisi', 'IT Teknisyeni'];
  const isAdmin = user && adminRoles.includes(user.role);

  const handleDeleteTicket = async (e) => {
    e.stopPropagation();
    const confirmMessage = t('deleteConfirm') || `"${ticket.title}" ticket'ını silmek istediğinizden emin misiniz?`;
    if (window.confirm(confirmMessage)) {
      try {
        await deleteTicket(ticket.id);
      } catch (error) {
        const errorMessage = t('deleteError') || 'Ticket silinirken hata oluştu!';
        alert(errorMessage);
      }
    }
    setShowAdminMenu(false);
    setContextMenu({ ...contextMenu, visible: false });
  };

  const handleCancelTicket = async (e) => {
    e.stopPropagation();
    try {
      // Sürükle-bırakın yaptığı isteğin aynısını yapıyoruz
      await updateTicketStatus(ticket.id, 'İptal');
      setContextMenu({ ...contextMenu, visible: false });
    } catch (error) {
      alert('İşlem başarısız: ' + error.message);
    }
  };

  const handleContextMenu = (e) => {
    e.preventDefault();
    setContextMenu({
      visible: true,
      x: e.pageX,
      y: e.pageY
    });
  };

  const handleEditTicket = (e) => {
    e.stopPropagation();
    onClick(ticket);
    setShowAdminMenu(false);
    setContextMenu({ ...contextMenu, visible: false });
  };

  // Menülerin dışına tıklandığında kapat
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowAdminMenu(false);
      }
      if (contextMenuRef.current && !contextMenuRef.current.contains(event.target)) {
        setContextMenu({ ...contextMenu, visible: false });
      }
    };

    if (showAdminMenu || contextMenu.visible) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showAdminMenu, contextMenu.visible]);

  // Backend'den gelen veri yapısına uygun alanları al
  const getPriorityText = () => {
    if (ticket.priority && typeof ticket.priority === 'object' && ticket.priority.priority_name) {
      return ticket.priority.priority_name;
    }
    return ticket.priority || 'Medium';
  };

  const getAssigneeInfo = () => {
    if (ticket.assigned_to_user && typeof ticket.assigned_to_user === 'object') {
      return {
        name: `${ticket.assigned_to_user.first_name} ${ticket.assigned_to_user.last_name}`,
        first_name: ticket.assigned_to_user.first_name,
        last_name: ticket.assigned_to_user.last_name
      };
    }
    return null;
  };

  const assignee = getAssigneeInfo();

  return (
    <>
      <div
        onContextMenu={handleContextMenu}
        className={`group relative bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded p-1 shadow-sm transition-all duration-200 cursor-pointer ${isDragging
          ? 'shadow-lg transform scale-105 opacity-90 pointer-events-none z-50 border-blue-300 dark:border-blue-500'
          : 'hover:shadow-sm hover:border-blue-300 dark:hover:border-blue-500'
          }`}
      >
        {/* Admin Menu Button */}
        {isAdmin && !isDragging && (
          <div className={`absolute top-0.5 right-0.5 z-10 transition-opacity ${showAdminMenu ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowAdminMenu(!showAdminMenu);
              }}
              className="w-4 h-4 flex items-center justify-center rounded bg-white/90 dark:bg-gray-800/90 text-gray-600 dark:text-gray-300 hover:bg-white dark:hover:bg-gray-700 transition-colors shadow-sm border border-gray-200 dark:border-gray-600"
              title="İşlemler"
            >
              <EllipsisVerticalIcon className="w-2.5 h-2.5" />
            </button>

            {showAdminMenu && (
              <div
                ref={menuRef}
                className="absolute top-3 right-0 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded shadow-lg py-0.5 z-[60] min-w-[60px] text-xs"
              >
                <button
                  onClick={handleEditTicket}
                  className="w-full px-1 py-0.5 text-xs text-left flex items-center gap-1 hover:bg-gray-50 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300"
                >
                  <PencilIcon className="w-2 h-2" />
                  {t('edit') || 'Düzenle'}
                </button>
                <button
                  onClick={handleDeleteTicket}
                  className="w-full px-1 py-0.5 text-xs text-left flex items-center gap-1 hover:bg-red-50 dark:hover:bg-red-900/20 text-red-600 dark:text-red-400"
                >
                  <TrashIcon className="w-2 h-2" />
                  {t('delete') || 'Sil'}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Card Content */}
        <div
          className="cursor-pointer"
          onClick={() => onClick(ticket)}
        >
          <div className="flex items-start justify-between mb-0.5">
            <span className="text-xs text-gray-500 dark:text-gray-400 font-mono">{ticket.id}</span>
          </div>
          <h4 className="text-xs font-medium text-gray-900 dark:text-white mb-1 leading-tight pr-3 line-clamp-2">{ticket.title}</h4>
          <div className="flex items-center justify-between">
            <span className={`text-[10px] px-1 py-0.5 rounded leading-none ${getPriorityColor(getPriorityText())}`}>
              {getPriorityText()}
            </span>
            {assignee && (
              <div className="w-3.5 h-3.5 bg-blue-500 rounded-full flex items-center justify-center text-white text-[10px]">
                {assignee.first_name?.charAt(0) || '?'}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Context Menu (Right Click) */}
      {contextMenu.visible && (
        <div
          ref={contextMenuRef}
          className="fixed bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded shadow-xl py-1 z-[9999] min-w-[120px]"
          style={{ top: contextMenu.y, left: contextMenu.x }}
        >
          <button
            onClick={handleEditTicket}
            className="w-full px-3 py-1.5 text-sm text-left flex items-center gap-2 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200"
          >
            <EyeIcon className="w-4 h-4" />
            Detayları Gör
          </button>
          <button
            onClick={handleCancelTicket}
            className="w-full px-3 py-1.5 text-sm text-left flex items-center gap-2 hover:bg-red-50 dark:hover:bg-red-900/20 text-red-600 dark:text-red-400 border-t border-gray-100 dark:border-gray-700"
          >
            <TrashIcon className="w-4 h-4" />
            İptal Et
          </button>
          {isAdmin && (
            <button
              onClick={handleDeleteTicket}
              className="w-full px-3 py-1.5 text-sm text-left flex items-center gap-2 hover:bg-red-100 dark:hover:bg-red-900/40 text-red-700 dark:text-red-300"
            >
              <TrashIcon className="w-4 h-4" />
              Sistemden Sil
            </button>
          )}
        </div>
      )}
    </>
  );
};

export default TicketCard;